// frontend/src/hooks/useTorusSpringVisualizer.js
import { useEffect, useState } from "react";

/**
 * Scène 3D du Tore-Ressorts — REÇUE du backend, jamais redéfinie ici.
 *
 * Voir docs/DOCTRINE_GEOMETRIE_CANONIQUE.md.
 *
 * ⚠️ Ce hook construisait sa propre disposition : 32 sphères sur un tore de
 * rayons 8/2, avec des couleurs et des raideurs de ressort tirées au hasard
 * (`Math.random()`). Le backend en avait deux autres — `generate_torus_spring_frame`
 * (24 sphères, sans ressorts) et `generate_torus_spring_system` (20 sphères,
 * avec ressorts, masses et physique). Trois géométries concurrentes sous le
 * même nom : le tick serveur simulait l'une, le navigateur en affichait une
 * autre.
 *
 * La géométrie canonique vient désormais de
 * `GET /api/geometry/torus_spring/initial` — la plus riche des trois, seule à
 * porter les ressorts et les masses. Le front la rend telle quelle.
 */
//: Forme du tore servant de repère visuel, en attendant `/initial`.
//:
//: ⚠️ Cet état initial valait `torus: null`, et `TorusSpring3D` lit
//: `torus.major_radius` DÈS LE PREMIER RENDU — avant que la requête n'ait pu
//: répondre. La page plantait donc systématiquement à l'ouverture sur
//: « Cannot read properties of null (reading 'major_radius') ».
//:
//: Un état initial doit être une valeur COMPLÈTE du même type que l'état final,
//: pas un marqueur d'absence. Les valeurs reprennent celles que le backend sert
//: (`geometry/torus_spring/__init__.py`), donc le repère ne saute pas quand les
//: vraies données arrivent.
const TORE_PAR_DEFAUT = { major_radius: 8, minor_radius: 3 };

export default function useTorusSpringVisualizer() {
  const [scene, setScene] = useState({
    spheres: [], springs: [], torus: TORE_PAR_DEFAUT,
  });

  useEffect(() => {
    const controller = new AbortController();

    fetch("/api/geometry/torus_spring/initial", { signal: controller.signal })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (!data || !Array.isArray(data.spheres)) {
          throw new Error("réponse /initial invalide");
        }
        setScene({
          spheres: data.spheres.map((s) => ({
            id: s.id,
            position: s.position,
            radius: s.radius ?? 0.3,
            color: s.color ?? [1, 1, 1],
          })),
          springs: (data.springs || []).map((sp) => ({
            sphere1: sp.sphere1 ?? sp.a,
            sphere2: sp.sphere2 ?? sp.b,
            stiffness: sp.stiffness ?? 0.5,
            color: sp.color ?? [1, 1, 1],
          })),
          torus: data.torus ?? TORE_PAR_DEFAUT,
        });
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          console.error("Erreur /initial torus_spring:", err);
        }
      });

    return () => controller.abort();
  }, []);

  return scene;
}
